package fr.manyverse.app.presentation.components.player_screen.character

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.presentation.components.ErrorState
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.PlayerNotesViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.notes
import manyverse.shared.generated.resources.notes_placeholder
import manyverse.shared.generated.resources.save
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PlayerNotesSheet(
    viewModel: PlayerNotesViewModel,
    onDismiss: () -> Unit
) {
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val notesState by viewModel.notesState.collectAsStateWithLifecycle()
    val saveState by viewModel.saveState.collectAsStateWithLifecycle()
    // Modifiable seulement une fois les notes chargées : enregistrer avant écraserait celles en base
    val loadedNotes = (notesState as? AsyncUiState.Success)?.data
    var text by remember(loadedNotes) { mutableStateOf(loadedNotes.orEmpty()) }

    LaunchedEffect(Unit) {
        viewModel.loadNotes()
    }

    LaunchedEffect(saveState) {
        if (saveState is AsyncUiState.Success) {
            onDismiss()
        }
    }

    ModalBottomSheet(
        modifier = Modifier.clearFocusOnTap(),
        onDismissRequest = onDismiss,
        sheetState = sheetState
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
                .padding(bottom = 24.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Text(
                text = stringResource(Res.string.notes),
                style = MaterialTheme.typography.titleMedium
            )
            when (val state = notesState) {
                is AsyncUiState.Error -> ErrorState(message = state.message, onRetry = viewModel::loadNotes)
                is AsyncUiState.Success -> NotesEditor(
                    text = text,
                    onTextChange = { text = it },
                    saveState = saveState,
                    onSave = { viewModel.saveNotes(text) }
                )
                else -> Box(
                    modifier = Modifier.fillMaxWidth().heightIn(min = 200.dp),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }
            }
        }
    }
}

@Composable
private fun NotesEditor(
    text: String,
    onTextChange: (String) -> Unit,
    saveState: AsyncUiState<Unit>,
    onSave: () -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        OutlinedTextField(
            value = text,
            onValueChange = onTextChange,
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 200.dp),
            placeholder = {
                Text(
                    text = stringResource(Res.string.notes_placeholder),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            },
            textStyle = MaterialTheme.typography.bodyMedium,
            shape = MaterialTheme.shapes.medium,
            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences)
        )
        if (saveState is AsyncUiState.Error) {
            Text(
                text = saveState.message,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.error
            )
        }
        Button(
            onClick = onSave,
            enabled = saveState !is AsyncUiState.Loading,
            modifier = Modifier.fillMaxWidth()
        ) {
            if (saveState is AsyncUiState.Loading) {
                CircularProgressIndicator(
                    modifier = Modifier.size(16.dp),
                    strokeWidth = 2.dp,
                    color = MaterialTheme.colorScheme.onPrimary
                )
            } else {
                Text(stringResource(Res.string.save))
            }
        }
    }
}
